import type { ReactNode } from 'react'

/**
 * The banner at the top of a console screen: a light card with a soft yellow wash on the left and
 * a thin yellow edge - the brand colour as an accent, not a background.
 */
export function AdminHero({
  icon,
  eyebrow,
  title,
  blurb,
  children,
}: {
  icon: ReactNode
  eyebrow: string
  title: string
  blurb: string
  /** Badges and buttons, on the right. */
  children?: ReactNode
}) {
  return (
    <section className="relative overflow-hidden rounded-2xl border border-ink-200/70 bg-gradient-to-r from-sun-300/25 via-white to-white p-5 shadow-card sm:p-6">
      <div className="absolute inset-y-0 left-0 w-1.5 bg-sun-400" aria-hidden />
      <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-sun-400 text-ink-900 shadow-sm">
            {icon}
          </span>
          <div className="min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-amber-700">{eyebrow}</div>
            <h1 className="text-2xl font-extrabold tracking-tight text-ink-900">{title}</h1>
            <p className="mt-0.5 text-sm text-ink-500">{blurb}</p>
          </div>
        </div>
        {children && <div className="flex shrink-0 flex-wrap items-center gap-2">{children}</div>}
      </div>
    </section>
  )
}

/** A small pill for the banner's right side. */
export function HeroBadge({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-ink-200 bg-white px-3 py-1.5 text-xs font-semibold text-ink-700">
      {children}
    </span>
  )
}
